import { useEffect, useState } from 'react';
import { useFeatureFlag, useStore } from 'providers';
import { useFetchFromService, usePageTitle } from 'hooks';
import { Offer } from 'models';
import Layout from 'layouts';
import { Alert, Grid, Typography } from '@mui/material';
import PerkzStoreList from 'components/PerkzStoreList';
import Loader from 'components/Loader';
import { pushAnalyticsEvent } from 'helpers/analytics';
import PurchaseFlowModal, { ModalState } from 'views/modals/PurchaseFlowModal';
import { LocalOfferOutlined } from '@mui/icons-material';
import { promotions } from 'constants/promotions';
import { StyledContainer, StyledBox, StyledBalanceCard } from './PerkzStore.styles';

const { isSaleOfferPeriod } = promotions;
const pageTitle = 'Perkz Store';

function PerkzStore() {
  usePageTitle(pageTitle);

  const { store } = useStore();
  const { userData, offersData } = store;

  const fetchOffersData = useFetchFromService('offersData');
  const allowSaleOffers = useFeatureFlag('allowSaleOffers') && isSaleOfferPeriod;

  useEffect(() => {
    if (!offersData) {
      fetchOffersData();
    }
  }, [offersData, fetchOffersData]);

  const [openModals, setOpenModals] = useState<ModalState>({
    perkzStore: false,
    purchaseVouchers: false,
    perkzWallet: false,
  });

  const [currentOffer, setCurrentOffer] = useState<Offer>({
    id: 0,
    title: '',
    points: 0,
    imageUrl: '',
    description: '',
    conditions: '',
    isAlcohol: false,
  });

  const handleViewOffer = (offer: Offer) => {
    setCurrentOffer(offer);
    setOpenModals({ ...openModals, perkzStore: true });
    pushAnalyticsEvent({
      event: 'storeOpenModal',
      offer: offer.title,
      reward: offer.title,
      price: offer.points,
    });
  };

  return (
    <Layout ariaLabel="Perkz Store page">
      {userData && offersData ? (
        <StyledBox>
          <StyledContainer aria-label="Perkz Store list">
            <Grid item xs={12} md={12}>
              <Typography variant="h1">Perkz Store</Typography>
            </Grid>

            <Grid item xs={12} md={12}>
              <Typography gutterBottom>
                Collect Zs and swap them for tasty Perkz. Remember, the more you visit, the more Zs
                you&apos;ll earn - and the better Perkz you can unlock (scroll down to see what you
                could get!)
              </Typography>
            </Grid>

            <Grid item xs={12} md={12}>
              <StyledBalanceCard points={userData.pointsDetails.points} pageTitle={pageTitle} />
            </Grid>

            {allowSaleOffers && (
              <Grid item xs={12} md={12}>
                <Alert severity="success" icon={<LocalOfferOutlined />}>
                  Bambini Perkz Sale now on! Save your Zs and snap up an amazing 50% off Bambini
                  Menu perkz until 12.08.24!
                </Alert>
              </Grid>
            )}

            <PerkzStoreList offers={offersData} onViewOffer={handleViewOffer} />
          </StyledContainer>

          <PurchaseFlowModal
            currentOffer={currentOffer}
            openModals={openModals}
            setOpenModals={setOpenModals}
          />
        </StyledBox>
      ) : (
        <Loader />
      )}
    </Layout>
  );
}

export default PerkzStore;
